'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import sharedStyles from '@/app/shared.module.scss';
import styles from './scheduleFilters.module.scss';

const ScheduleFilters = ({ groupedEvents, allEvents, onFilterChange, selectedDate: controlledDate, selectedVenue: controlledVenue }) => {
	const defaultDateLabel = 'Filter by day';
	const defaultVenueLabel = 'Filter by venue';

	const [internalDate, setInternalDate] = useState(defaultDateLabel);
	const [internalVenue, setInternalVenue] = useState(defaultVenueLabel);
	const [isVenueOpen, setIsVenueOpen] = useState(false);

	const venueRef = useRef(null);

	// Support controlled mode (e.g. from URL): use props when provided, else internal state
	const selectedDate = controlledDate !== undefined ? (controlledDate ?? defaultDateLabel) : internalDate;
	const selectedVenue = controlledVenue !== undefined ? (controlledVenue ?? defaultVenueLabel) : internalVenue;

	const dateOptions = useMemo(() => {
		if (!groupedEvents || !Array.isArray(groupedEvents)) return [];

		const orderedUniqueDates = [];
		const seen = new Set();

		groupedEvents.forEach(group => {
			if (group?.displayDate && !seen.has(group.displayDate)) {
				seen.add(group.displayDate);
				orderedUniqueDates.push(group.displayDate);
			}
		});

		return orderedUniqueDates;
	}, [groupedEvents]);

	// Same precedence as display: event venue → session location → event location → session linked venue (so dropdown shows all venues visible on the page)
	const getVenueName = (event) => {
		return event?.venue?.data?.name
			|| event?.linked_session?.data?.location
			|| event?.location
			|| event?.linked_session?.data?.venue?.data?.name
			|| null;
	};

	// Build venue options from all events (not just groupedEvents) so venues like XXV that only appear on events with date-from-session are included
	const venueOptions = useMemo(() => {
		const eventsToScan = Array.isArray(allEvents) && allEvents.length > 0 ? allEvents : null;
		if (!eventsToScan) {
			if (!groupedEvents || !Array.isArray(groupedEvents)) return [];
			// Fallback: collect from grouped events
			const venues = new Set();
			groupedEvents.forEach(group => {
				if (group?.events && Array.isArray(group.events)) {
					group.events.forEach(event => {
						const venueName = getVenueName(event);
						if (venueName) venues.add(venueName);
					});
				}
			});
			return Array.from(venues).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
		}
		const venues = new Set();
		eventsToScan.forEach(event => {
			const venueName = getVenueName(event);
			if (venueName) venues.add(venueName);
		});
		return Array.from(venues).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
	}, [allEvents, groupedEvents]);

	// Only sync to parent from internal state when uncontrolled
	useEffect(() => {
		if (controlledDate === undefined && controlledVenue === undefined) {
			onFilterChange({
				date: internalDate === defaultDateLabel ? null : internalDate,
				venue: internalVenue === defaultVenueLabel ? null : internalVenue,
			});
		}
	}, [internalDate, internalVenue, onFilterChange, defaultDateLabel, defaultVenueLabel, controlledDate, controlledVenue]);

	const handleDateSelect = (date) => {
		const value = date === 'All Days' ? defaultDateLabel : date;
		if (controlledDate === undefined) setInternalDate(value);
		onFilterChange({
			date: value === defaultDateLabel ? null : value,
			venue: selectedVenue === defaultVenueLabel ? null : selectedVenue,
		});
	};

	const handleVenueSelect = (venue) => {
		const value = venue === 'All venues' ? defaultVenueLabel : venue;
		if (controlledVenue === undefined) setInternalVenue(value);
		onFilterChange({
			date: selectedDate === defaultDateLabel ? null : selectedDate,
			venue: value === defaultVenueLabel ? null : value,
		});
		setIsVenueOpen(false);
	};

	const handleClickOutside = useCallback((event) => {
		if (venueRef.current && !venueRef.current.contains(event.target)) {
			setIsVenueOpen(false);
		}
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);


	const toggleVenue = () => setIsVenueOpen(prev => !prev);

	const isDateFiltered = selectedDate !== defaultDateLabel;
	const isVenueFiltered = selectedVenue !== defaultVenueLabel;

	// Helper function to parse displayDate (format: "EEEE MMM d" like "Thursday Feb 23")
	// and extract dayLabel, dayNumber, monthLabel
	const parseDateString = (dateString) => {
		if (!dateString) return null;

		const parts = dateString.split(' ');
		if (parts.length !== 3) return null;

		const [dayName, monthLabel, dayNumber] = parts;

		// Convert day name to abbreviation (matching FullLineup format)
		const dayAbbrMap = {
			'Monday': 'Mon',
			'Tuesday': 'Tues',
			'Wednesday': 'Wed',
			'Thursday': 'Thur',
			'Friday': 'Fri',
			'Saturday': 'Sat',
			'Sunday': 'Sun'
		};

		const dayLabel = dayAbbrMap[dayName] || dayName.substring(0, 3);

		return {
			dayLabel,
			dayNumber,
			monthLabel,
			displayDate: dateString
		};
	};

	return (
		<div className={styles.filters}>

			{/* Date Filter - Button Style (matching FullLineup) */}
			{dateOptions.length > 0 && venueOptions.length > 0 && (
				<div className={clsx(sharedStyles.filters, styles.dateFilterContainer)} suppressHydrationWarning>
					<div className={clsx(sharedStyles.label_wrap)}>
						<div className={clsx(sharedStyles.label)}>Filter <span className={clsx('hidden sm:inline-flex!')}>by day</span></div>
						<Chevron className={clsx(sharedStyles.chevron)} />
					</div>

					<div className={clsx(sharedStyles.filterButtonsContainer)}>
						<span
							onClick={() => handleDateSelect('All Days')}
							className={clsx(sharedStyles.item, sharedStyles.whiteButton, sharedStyles.pinkText, sharedStyles.allDays)}
						>
							All Days
						</span>

						{dateOptions.map((date) => {
							const isHighlighted = date === selectedDate;
							const parsedDate = parseDateString(date);

							return (
								<span
									key={date}
									onClick={() => handleDateSelect(date)}
									className={clsx(sharedStyles.item, sharedStyles.whiteButton, {
										[sharedStyles.highlighted]: isHighlighted,
									})}
								>
									{parsedDate ? (
										<>
											<span className={clsx(sharedStyles.dayLabel)}>{parsedDate.dayLabel}</span> {parsedDate.dayNumber} {parsedDate.monthLabel}
										</>
									) : (
										date
									)}
								</span>
							);
						})}

						{/* Venue Filter */}
						<div className={clsx(styles.filter, styles.venueFilter)} ref={venueRef} suppressHydrationWarning>
							<div
								className={clsx(styles.selected, { [styles.open]: isVenueOpen })}
								onClick={toggleVenue}
							>
								<div className={clsx(styles.inner, styles.space)}>
									<span className={styles.value}>{selectedVenue}</span>
									<Chevron className={clsx(styles.chevron, { [styles.rotated]: isVenueOpen })} />
								</div>
								<div className={clsx(styles.inner, styles.initial)}>
									<span className={styles.value}>{selectedVenue}</span>
									<Chevron className={clsx(styles.chevron, { [styles.rotated]: isVenueOpen })} />
								</div>
								<div className={clsx(styles.inner, styles.second)}>
									<span className={styles.value}>{selectedVenue}</span>
									<Chevron className={clsx(styles.chevron, { [styles.rotated]: isVenueOpen })} />
								</div>
							</div>
							<ul className={clsx(styles.dropdown, { [styles.open]: isVenueOpen })}>
								<li
									key="All venues"
									className={clsx({ [styles.active]: selectedVenue === defaultVenueLabel })}
									onClick={() => handleVenueSelect('All venues')}
								>
									All venues
								</li>
								{venueOptions.map((venue) => (
									<li
										key={venue}
										className={clsx({ [styles.active]: venue === selectedVenue })}
										onClick={() => handleVenueSelect(venue)}
									>
										{venue}
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ScheduleFilters;